import { ShadowlessElement, WithDisposable } from '@blocksuite/block-std';
import { SignalWatcher } from '@lit-labs/preact-signals';
import { createReactComponentFromLit } from '@pique/component';
import type { BreakdownStep } from '@pique/core/modules/properties/services/schema';
import { css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import React from 'react';

export type ActionPlanStep = BreakdownStep;

export interface ActionPlanTimelineEditDetail {
  stepId: string;
  field: 'title' | 'description' | 'completed';
  value: string | boolean;
  steps: ActionPlanStep[];
}

@customElement('pique-action-plan-timeline')
export class PiqueActionPlanTimeline extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = css`
    :host {
      display: block;
    }

    .timeline-list {
      position: relative;
      display: flex;
      flex-direction: column;
      gap: 0;
      margin-top: 4px;
      padding-bottom: 0;
    }

    .timeline-item {
      position: relative;
      display: flex;
      gap: 10px;
      padding: 0;
      transition: all 0.2s ease;
    }

    .remove-btn {
      position: absolute;
      left: -18px;
      top: 2px;
      width: 16px;
      height: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--affine-text-secondary-color);
      cursor: pointer;
      opacity: 0;
      transition: opacity 0.2s ease, color 0.2s ease;
      background: transparent;
      border: none;
      padding: 0;
    }

    .timeline-item:hover .remove-btn {
      opacity: 1;
    }

    .remove-btn:hover {
      color: var(--affine-error-color);
    }

    .add-step-item {
      cursor: pointer;
      opacity: 0.7;
      transition: opacity 0.2s ease;
    }
    
    .add-step-item:hover {
      opacity: 1;
    }
    
    .add-step-item {
      margin-top: 6px;
    }

    .timeline-dot.add-dot {
      border: 1.5px dashed var(--affine-border-color);
      background: var(--affine-background-primary-color);
      color: var(--affine-text-secondary-color);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .timeline-dot.add-dot:hover {
      border-color: var(--affine-brand-color);
      color: var(--affine-brand-color);
      background: var(--affine-hover-color);
    }

    .timeline-line.dotted {
      border-left: 1.5px dotted var(--affine-border-color);
      background: transparent;
      width: 0;
    }

    .timeline-line-container {
      position: relative;
      width: 18px;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .timeline-dot {
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: var(--affine-background-primary-color);
      border: 1.5px solid var(--affine-border-color);
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      z-index: 2;
      flex-shrink: 0;
      margin-top: 1px;
      transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease;
      cursor: pointer;
      outline: none;
      box-shadow: none;
    }

    .timeline-dot.completed {
      background: var(--pique-green-gradient-solid);
      border-color: var(--pique-green-primary);
      color: white;
      box-shadow: var(--pique-green-shadow-strong);
    }

    .timeline-dot::after {
      content: '';
      width: 10px;
      height: 10px;
      mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'%3E%3C/polyline%3E%3C/svg%3E");
      -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'%3E%3C/polyline%3E%3C/svg%3E");
      mask-size: contain;
      -webkit-mask-size: contain;
      mask-repeat: no-repeat;
      -webkit-mask-repeat: no-repeat;
      mask-position: center;
      -webkit-mask-position: center;
      background-color: var(--affine-black-30);
      opacity: 1;
      transition: opacity 0.2s ease, background-color 0.2s ease;
    }

    .timeline-dot:hover::after {
      background-color: var(--affine-icon-color);
      opacity: 1;
    }

    .timeline-dot.completed::after {
      background-color: white;
      opacity: 1;
    }

    .timeline-dot.active {
      background: var(--affine-background-secondary-color);
      border-color: var(--affine-brand-color);
      box-shadow: 0 6px 18px rgba(139, 92, 246, 0.32);
    }

    .timeline-dot.active::before {
      content: '';
      position: absolute;
      top: -4px;
      left: -4px;
      right: -4px;
      bottom: -4px;
      border-radius: 50%;
      background: rgba(139, 92, 246, 0.3);
      animation: pulse-wave 2s ease-out 1 forwards;
      z-index: -1;
      opacity: 0;
    }

    .timeline-dot.add-dot::after {
      display: none;
    }

    .timeline-dot:hover:not(.completed),
    .timeline-dot:focus-visible:not(.completed) {
      border-color: var(--affine-brand-color);
      transform: scale(1.1);
    }

    .timeline-dot:focus-visible {
      box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.2);
    }

    .timeline-dot[aria-disabled='true'] {
      cursor: default;
      box-shadow: none;
    }

    .timeline-dot[aria-disabled='true']:hover {
      transform: none;
      border-color: var(--affine-border-color);
    }

    @keyframes pulse-wave {
      0% { transform: scale(1); opacity: 0.6; }
      100% { transform: scale(2.5); opacity: 0; }
    }

    .timeline-line {
      position: absolute;
      top: 20px;
      bottom: -10px;
      left: 50%;
      transform: translateX(-50%);
      width: 1.5px;
      background: var(--affine-border-color);
    }

    .timeline-item:last-child .timeline-line {
      display: none;
    }

    .timeline-content {
      flex: 1;
      min-width: 0;
    }

    .timeline-card {
      background: transparent;
      border: none;
      padding: 0;
      position: relative;
      cursor: default;
    }

    .step-header {
      display: flex;
      align-items: center;
      margin-bottom: 2px;
      gap: 8px;
    }

    .step-title {
      font-size: 13px;
      font-weight: 500;
      color: var(--affine-text-secondary-color);
      line-height: 1.4;
      word-wrap: break-word;
      outline: none;
      background: transparent;
      border: 1px solid transparent;
      border-radius: 4px;
      padding: 2px 4px;
      margin: -2px -4px;
      transition: all 0.15s ease;
    }

    .timeline-item.active .step-title {
      color: var(--affine-text-primary-color);
      font-weight: 600;
    }

    .timeline-item.completed .step-title {
      color: var(--affine-text-disable-color);
      text-decoration: line-through;
    }

    .timeline-item.completed .step-description {
      color: var(--affine-text-disable-color);
    }

    .step-title[contenteditable='true'],
    .step-description[contenteditable='true'] {
      cursor: text;
    }

    .step-title.editing {
      background: var(--affine-background-primary-color);
      border-color: var(--affine-brand-color);
      box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.1);
    }

    .step-description {
      font-size: 13px;
      color: var(--affine-text-secondary-color);
      line-height: 1.5;
      word-wrap: break-word;
      margin-top: 4px;
      outline: none;
      background: transparent;
      border: 1px solid transparent;
      border-radius: 4px;
      padding: 4px;
      margin-left: -4px;
      margin-right: -4px;
      transition: all 0.15s ease;
      min-height: 20px;
    }

    .step-description.editing {
      background: var(--affine-background-primary-color);
      border-color: var(--affine-border-color);
      color: var(--affine-text-primary-color);
    }

    .ai-help-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      padding: 4px 10px;
      border-radius: 12px;
      flex-shrink: 0;
      background: var(--pique-green-gradient-light);
      border: 1px solid var(--pique-green-border);
      color: var(--affine-text-primary-color);
      font-size: 11px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;
      box-shadow: var(--pique-green-shadow-subtle);
    }

    .ai-help-btn:hover {
      background: var(--pique-green-gradient-hover);
      border-color: var(--pique-green-border-hover);
      box-shadow: var(--pique-green-shadow-medium);
      transform: translateY(-1px);
    }

    .ai-help-btn:active {
      transform: translateY(0);
    }

    .ai-help-icon {
      width: 14px;
      height: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .ai-help-icon svg {
      width: 14px;
      height: 14px;
    }

  `;

  @property({ attribute: false })
  accessor steps: ActionPlanStep[] = [];

  @property({ type: Boolean })
  accessor editable = true;

  @state()
  private accessor internalSteps: ActionPlanStep[] = [];

  @state()
  private accessor editingStepId: string | null = null;

  @state()
  private accessor editingField: 'title' | 'description' | null = null;

  override updated(changed: Map<string, unknown>): void {
    super.updated?.(changed as any);
    if (changed.has('steps')) {
      this.internalSteps = (this.steps ?? []).map(step => ({ ...step }));
    }
  }

  private getNextActiveIndex(): number {
    return this.internalSteps.findIndex(step => !step.completed);
  }

  private emitEdit(
    stepId: string,
    field: 'title' | 'description' | 'completed',
    value: string | boolean,
    steps: ActionPlanStep[],
  ) {
    this.dispatchEvent(
      new CustomEvent<ActionPlanTimelineEditDetail>('pique-timeline-step-edit', {
        detail: { stepId, field, value, steps },
        bubbles: true,
        composed: true,
      })
    );
  }

  private handleEdit(stepId: string, field: 'title' | 'description', value: string) {
    const trimmed = field === 'title' ? value.trim() : value;
    const updatedSteps = this.internalSteps.map(step =>
      step.id === stepId ? { ...step, [field]: trimmed } : step
    );
    this.internalSteps = updatedSteps;
    this.emitEdit(stepId, field, trimmed, updatedSteps);
  }

  private startEditing(stepId: string, field: 'title' | 'description') {
    if (!this.editable) return;
    this.editingStepId = stepId;
    this.editingField = field;
  }

  private stopEditing() {
    this.editingStepId = null;
    this.editingField = null;
  }

  private toggleCompletion(stepId: string, event?: Event) {
    if (!this.editable) return;

    event?.stopPropagation();

    const updatedSteps = this.internalSteps.map(step =>
      step.id === stepId ? { ...step, completed: !step.completed } : step
    );

    this.internalSteps = updatedSteps;
    const toggled = updatedSteps.find(step => step.id === stepId)?.completed ?? false;
    this.emitEdit(stepId, 'completed', toggled, updatedSteps);
  }
  
  private addStep() {
    if (!this.editable) return;
    const newStep: ActionPlanStep = {
      id: crypto.randomUUID(),
      title: '',
      description: '',
      completed: false
    };
    const updatedSteps = [...this.internalSteps, newStep];
    this.internalSteps = updatedSteps;
    this.emitEdit(newStep.id, 'title', '', updatedSteps);
    
    // Auto-focus new step title
    setTimeout(() => {
        this.startEditing(newStep.id, 'title');
    }, 50);
  }
  
  private removeStep(index: number, event: Event) {
    if (!this.editable) return;
    event.stopPropagation();
    const stepToRemove = this.internalSteps[index];
    if (!stepToRemove) return;
    const updatedSteps = this.internalSteps.filter((_, i) => i !== index);
    this.internalSteps = updatedSteps;
    this.emitEdit(stepToRemove.id, 'title', '', updatedSteps);
  }

  private handleDotKeydown(event: KeyboardEvent, stepId: string) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleCompletion(stepId, event);
    }
  }

  private handleAiHelp(step: ActionPlanStep, event: Event) {
    event.stopPropagation();
    const detail = {
      stepTitle: step.title,
      stepDescription: step.description,
      stepId: step.id,
    };
    this.dispatchEvent(
      new CustomEvent('pique-timeline-ai-help', {
        detail,
        bubbles: true,
        composed: true,
      })
    );
    window.dispatchEvent(
      new CustomEvent('pique-open-ai-chat', {
        detail: {
          closeModal: true,
          autoPrompt: {
            userPrompt: `Could you help me get started with "${step.title}"`,
            systemHint: [
              'You are a helpful productivity assistant. The user is working on a specific action item step and needs help getting started.',
              '',
              `Current task step: "${step.title}"`,
              step.description ? `Details: "${step.description}"` : '',
              '',
              'Provide actionable, practical guidance in Markdown:',
              '### Getting Started',
              '',
              '**First Steps**',
              '- 2-3 concrete actions to begin immediately',
              '',
              '**Key Considerations**',
              '- Important things to keep in mind',
              '',
              '**Quick Wins**',
              '- Easy ways to build momentum',
              '',
              'Keep your response focused and actionable. Be encouraging but concise.',
            ].filter(Boolean).join('\n'),
          },
        },
      })
    );
  }

  private renderAiHelpButton(step: ActionPlanStep) {
    return html`
      <button
        class="ai-help-btn"
        @click=${(e: Event) => this.handleAiHelp(step, e)}
        title="Get help from Pique"
      >
        <span class="ai-help-icon">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.2812 5.49104C11.2403 5.13024 10.9353 4.85751 10.5722 4.85714C10.2091 4.85677 9.90345 5.12887 9.86185 5.48959C9.59131 7.83515 8.89003 9.48448 7.75868 10.6158C6.62734 11.7472 4.97801 12.4485 2.63244 12.719C2.27173 12.7606 1.99963 13.0662 2 13.4293C2.00037 13.7924 2.2731 14.0975 2.63389 14.1383C4.94069 14.3996 6.62508 15.1006 7.78328 16.2379C8.93713 17.3709 9.65305 19.0198 9.85994 21.3489C9.89271 21.7178 10.2019 22.0004 10.5722 22C10.9425 21.9996 11.2511 21.7162 11.2831 21.3473C11.4813 19.0565 12.1966 17.3729 13.3562 16.2133C14.5157 15.0537 16.1994 14.3385 18.4902 14.1402C18.8591 14.1083 19.1424 13.7997 19.1429 13.4294C19.1433 13.0591 18.8606 12.7499 18.4918 12.7171C16.1627 12.5102 14.5137 11.7943 13.3807 10.6404C12.2435 9.48222 11.5425 7.79783 11.2812 5.49104Z" fill="currentColor"/>
            <path d="M18.9427 2.24651C18.9268 2.1062 18.8082 2.00014 18.667 2C18.5257 1.99986 18.4069 2.10567 18.3907 2.24595C18.2855 3.15811 18.0128 3.79952 17.5728 4.23949C17.1329 4.67946 16.4914 4.95218 15.5793 5.05739C15.439 5.07356 15.3332 5.19241 15.3333 5.33362C15.3335 5.47482 15.4395 5.59345 15.5798 5.60935C16.4769 5.71096 17.132 5.98357 17.5824 6.42584C18.0311 6.86644 18.3095 7.50771 18.39 8.41347C18.4027 8.55691 18.523 8.66683 18.667 8.66667C18.811 8.6665 18.931 8.55632 18.9434 8.41284C19.0205 7.52199 19.2987 6.86723 19.7496 6.41629C20.2006 5.96534 20.8553 5.68719 21.7462 5.61008C21.8896 5.59766 21.9998 5.47765 22 5.33365C22.0002 5.18964 21.8902 5.06939 21.7468 5.05664C20.841 4.97619 20.1998 4.69777 19.7592 4.24905C19.3169 3.79864 19.0443 3.1436 18.9427 2.24651Z" fill="currentColor"/>
          </svg>
        </span>
        <span>Help me start</span>
      </button>
    `;
  }

  override render() {
    const steps = this.internalSteps ?? [];
    const activeIndex = this.getNextActiveIndex();

    return html`
      <div class="timeline-list">
        ${repeat(
      steps,
      step => step.id,
      (step, index) => {
        const isCompleted = Boolean(step.completed);
        const isActive = !isCompleted && activeIndex === index;
        const isEditing = this.editingStepId === step.id;
        const titleEditing = isEditing && this.editingField === 'title';
        const descriptionEditing = isEditing && this.editingField === 'description';

        return html`
              <div class="timeline-item ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}">
                <button
                    class="remove-btn"
                    @click=${(e: Event) => this.removeStep(index, e)}
                    title="Remove step"
                >
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: block;">
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                </button>
                <div class="timeline-line-container">
                  <div
                    class="timeline-dot ${isCompleted ? 'completed' : ''} ${isActive ? 'active' : ''}"
                    role="button"
                    tabindex=${this.editable ? '0' : '-1'}
                    aria-pressed=${isCompleted ? 'true' : 'false'}
                    aria-disabled=${this.editable ? 'false' : 'true'}
                    aria-label=${`Mark step ${index + 1} as ${isCompleted ? 'incomplete' : 'complete'}`}
                    @click=${(event: Event) => this.toggleCompletion(step.id, event)}
                    @keydown=${(event: KeyboardEvent) => this.handleDotKeydown(event, step.id)}
                  ></div>
                  <div class="timeline-line ${index === steps.length - 1 ? 'dotted' : ''}"></div>
                </div>

                <div class="timeline-content">
                  <div class="timeline-card">
                    <div class="step-header">
                      <div
                        class="step-title ${titleEditing ? 'editing' : ''}"
                        contenteditable=${this.editable ? 'true' : 'false'}
                        @focus=${() => this.startEditing(step.id, 'title')}
                        @blur=${(e: FocusEvent) => {
            const target = e.target as HTMLElement;
            this.handleEdit(step.id, 'title', target.textContent || '');
            this.stopEditing();
          }}
                        @keydown=${(e: KeyboardEvent) => {
            e.stopPropagation();
            if (e.key === 'Enter') {
              e.preventDefault();
              (e.target as HTMLElement).blur();
            }
          }}
                        @keypress=${(e: KeyboardEvent) => {
            e.stopPropagation();
          }}
                        @keyup=${(e: KeyboardEvent) => {
            e.stopPropagation();
          }}
                      >${step.title}</div>
                      ${isActive ? this.renderAiHelpButton(step) : ''}
                    </div>
                    <div
                      class="step-description ${descriptionEditing ? 'editing' : ''}"
                      contenteditable=${this.editable ? 'true' : 'false'}
                      @focus=${() => this.startEditing(step.id, 'description')}
                      @keydown=${(e: KeyboardEvent) => {
            e.stopPropagation();
          }}
                      @keypress=${(e: KeyboardEvent) => {
            e.stopPropagation();
          }}
                      @keyup=${(e: KeyboardEvent) => {
            e.stopPropagation();
          }}
                      @blur=${(e: FocusEvent) => {
            const target = e.target as HTMLElement;
            this.handleEdit(step.id, 'description', target.textContent || '');
            this.stopEditing();
          }}
                    >${step.description}</div>
                  </div>
                </div>
              </div>
            `;
      }
    )}
        <div class="timeline-item add-step-item" @click=${() => this.addStep()}>
            <div class="timeline-line-container">
                <div class="timeline-dot add-dot"></div>
            </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pique-action-plan-timeline': PiqueActionPlanTimeline;
  }
}

export const ActionPlanTimeline = (createReactComponentFromLit({
  react: React,
  elementClass: PiqueActionPlanTimeline,
  events: {
    onStepEdit: 'pique-timeline-step-edit',
    onAiHelp: 'pique-timeline-ai-help',
  },
}) as unknown) as React.FC<{
  steps: ActionPlanStep[];
  editable?: boolean;
  onStepEdit?: (event: CustomEvent<ActionPlanTimelineEditDetail>) => void;
  onAiHelp?: (event: CustomEvent<{ stepTitle: string; stepDescription: string; stepId: string }>) => void;
  className?: string;
}>;
